import React, { useEffect, useRef, useState } from 'react';
import { Car, Clock, CreditCard, Loader2, Receipt } from 'lucide-react';
import { Modal } from './Modal';
import NumberInput from './NumberInput';
import { RelazioneGiornalieraService, ServizioFiduciarioService } from '../services/apiService';
import { useToast } from './ToastContext';

// Rettifica di un report dall'agenda del collaboratore: stessi valori dell'approvazione
// (ore e km approvati, importi delle spese, anche quelle con carta aziendale). Salvando il
// report risulta approvato con i nuovi valori e il BE registra il prima/dopo nell'audit.
// I campi non mostrati (ore da fatturare, auto aziendale) vengono reinviati invariati.

type Props = {
    relazioneId: number;
    onClose: () => void;
    onSaved: () => void;
};

const num = (v: any): number => (v === '' || v === null || v === undefined ? 0 : Number(v) || 0);

export const ModificaReportModal: React.FC<Props> = ({ relazioneId, onClose, onSaved }) => {
    const { addToast } = useToast();
    const [report, setReport] = useState<any | null>(null);
    const [spese, setSpese] = useState<any[]>([]);
    const [oreApprovate, setOreApprovate] = useState<any>('');
    const [kmApprovati, setKmApprovati] = useState<any>('');
    const [buonoPasto, setBuonoPasto] = useState(true);
    const [saving, setSaving] = useState(false);
    // onClose arriva come arrow inline: fuori dalle dipendenze, altrimenti ogni render del
    // padre ricaricherebbe il report perdendo le modifiche.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        let vivo = true;
        RelazioneGiornalieraService.getById(relazioneId)
            .then((r: any) => {
                if (!vivo) return;
                setReport(r);
                const extra = r.oreExtraApprovate ?? r.oreExtra ?? 0;
                // Turno: le ore pagate sono l'override del turno, non oreApprovateTotali.
                const oreDefault = r.turnoId ? r.turnoOreCollaboratoreOverride : r.oreApprovateTotali;
                setOreApprovate(oreDefault ?? Math.round(((r.oreGps || 0) + extra) * 100) / 100);
                setKmApprovati(r.kmApprovati ?? r.kmPercorsi ?? 0);
                const tutte: any[] = r.speseEspanso || [];
                setBuonoPasto(r.stato !== 'APPROVATO' || tutte.some(s => s.buonoPastoAuto));
                // Le righe buono pasto automatiche le ricrea il BE: qui solo quelle del collaboratore.
                setSpese(tutte.filter(s => !s.buonoPastoAuto).map(s => ({
                    ...s,
                    importoApprovato: s.importoApprovato ?? (s.anticipatoAzienda ? 0 : s.importo),
                    importoCarta: s.importo,
                })));
            })
            .catch(() => { addToast('Errore caricamento report', 'error'); onCloseRef.current(); });
        return () => { vivo = false; };
    }, [relazioneId, addToast]);

    const setSpesa = (id: number, campo: string, v: any) =>
        setSpese(prev => prev.map(s => s.id === id ? { ...s, [campo]: v } : s));

    const salva = async () => {
        if (!report) return;
        if (!window.confirm('Salvi le modifiche? Il report risulterà approvato con questi valori.')) return;
        setSaving(true);
        try {
            const isTurno = !!report.turnoId;
            // Per i turni le ore pagate si salvano sul turno, come nella scheda del servizio.
            if (isTurno) await ServizioFiduciarioService.setOreCollaboratore(report.turnoId, num(oreApprovate));
            await RelazioneGiornalieraService.updateRevisione(report.id, {
                oreExtraApprovate: num(report.oreExtraApprovate ?? report.oreExtra),
                oreApprovateTotali: isTurno ? null : num(oreApprovate),
                oreFatturabiliApprovate: isTurno ? 0 : num(report.oreFatturabiliApprovate),
                kmApprovati: num(kmApprovati),
                autoAziendale: report.autoAziendale,
                applicaBuonoPasto: buonoPasto,
                speseApprovate: spese.map(s => ({
                    id: s.id,
                    importoApprovato: s.anticipatoAzienda ? 0 : num(s.importoApprovato),
                    anticipatoAzienda: !!s.anticipatoAzienda,
                    importoCarta: s.anticipatoAzienda ? num(s.importoCarta) : null,
                })),
            });
            addToast('Report aggiornato', 'success');
            onSaved();
        } catch (e: any) {
            addToast(e?.response?.data?.message || 'Errore salvataggio', 'error');
        } finally {
            setSaving(false);
        }
    };

    const data = report?.dataRelazione ? new Date(report.dataRelazione).toLocaleDateString('it-IT') : '';

    return (
        <Modal title={`Modifica report${data ? ` del ${data}` : ''}`} onClose={saving ? undefined : onClose}>
            {!report ? (
                <div className="py-10 flex justify-center"><Loader2 className="animate-spin text-blue-500" size={24} /></div>
            ) : (
                <div className="space-y-4 text-sm">
                    {report.praticaNome && (
                        <p className="text-xs text-gray-500 truncate">{report.praticaNome}</p>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                        <div className="border border-gray-200 rounded-lg p-3">
                            <p className="text-[10px] font-semibold text-gray-500 uppercase flex items-center gap-1 mb-1"><Clock size={11} /> Ore</p>
                            <p className="text-[11px] text-gray-400 mb-1.5">
                                Dichiarate: {Math.round(((report.oreGps || 0) + (report.oreExtra || 0)) * 100) / 100} h
                            </p>
                            <label className="text-[10px] font-semibold text-blue-700 uppercase">Approvate</label>
                            <div className="flex items-center gap-1 mt-0.5">
                                <NumberInput min={0} step="0.25" value={oreApprovate}
                                             onChange={e => setOreApprovate(e.target.value === '' ? '' : Number(e.target.value))}
                                             className="w-full p-1.5 text-center font-bold border border-blue-200 rounded outline-none focus:ring-1 ring-blue-400" />
                                <span className="text-xs text-blue-600 font-semibold">h</span>
                            </div>
                        </div>
                        <div className="border border-gray-200 rounded-lg p-3">
                            <p className="text-[10px] font-semibold text-gray-500 uppercase flex items-center gap-1 mb-1"><Car size={11} /> Km</p>
                            <p className="text-[11px] text-gray-400 mb-1.5">Dichiarati: {report.kmPercorsi || 0} km</p>
                            <label className="text-[10px] font-semibold text-blue-700 uppercase">Approvati</label>
                            <div className="flex items-center gap-1 mt-0.5">
                                <NumberInput min={0} maxDecimals={1} value={kmApprovati}
                                             onChange={e => setKmApprovati(e.target.value === '' ? '' : Number(e.target.value))}
                                             className="w-full p-1.5 text-center font-bold border border-blue-200 rounded outline-none focus:ring-1 ring-blue-400" />
                                <span className="text-xs text-blue-600 font-semibold">km</span>
                            </div>
                        </div>
                    </div>

                    {spese.length > 0 && (
                        <div>
                            <p className="text-[10px] font-semibold text-gray-500 uppercase flex items-center gap-1 mb-1.5"><Receipt size={11} /> Spese</p>
                            <div className="space-y-1.5">
                                {spese.map(s => (
                                    <div key={s.id} className={`flex items-center gap-2 p-2 rounded-lg border ${s.anticipatoAzienda ? 'bg-purple-50/50 border-purple-200' : 'border-gray-200'}`}>
                                        {s.anticipatoAzienda ? <CreditCard size={13} className="text-purple-600 shrink-0" /> : <Receipt size={13} className="text-gray-400 shrink-0" />}
                                        <div className="flex-1 min-w-0">
                                            <div className="font-semibold text-gray-700 text-xs truncate">{s.tipo || 'Spesa'}</div>
                                            <div className="text-[11px] text-gray-400">
                                                {s.anticipatoAzienda ? 'Carta aziendale' : 'Di tasca'} · dichiarati €{Number(s.importo || 0).toFixed(2)}
                                            </div>
                                        </div>
                                        <span className="text-xs font-semibold text-gray-500">€</span>
                                        <NumberInput min={0} step="0.01"
                                                     value={s.anticipatoAzienda ? s.importoCarta : s.importoApprovato}
                                                     onChange={e => setSpesa(s.id, s.anticipatoAzienda ? 'importoCarta' : 'importoApprovato',
                                                         e.target.value === '' ? '' : Number(e.target.value))}
                                                     title={s.anticipatoAzienda ? 'Importo pagato con carta aziendale' : 'Importo da rimborsare'}
                                                     className="w-20 p-1 text-center font-bold text-xs border border-blue-200 rounded outline-none focus:ring-1 ring-blue-400" />
                                    </div>
                                ))}
                            </div>
                            <p className="text-[11px] text-gray-400 mt-1">
                                Per la carta aziendale l'importo è l'uscita in contabilità; per le spese di tasca è il rimborso al collaboratore.
                            </p>
                        </div>
                    )}

                    <label className="flex items-center gap-2 text-xs text-gray-600">
                        <input type="checkbox" checked={buonoPasto} onChange={e => setBuonoPasto(e.target.checked)} />
                        Buono pasto automatico (se le ore raggiungono la soglia)
                    </label>

                    <div className="flex gap-2 pt-2">
                        <button type="button" onClick={onClose} disabled={saving}
                                className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg font-medium text-sm disabled:opacity-50">
                            Annulla
                        </button>
                        <button type="button" onClick={salva} disabled={saving}
                                className="flex-1 px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg font-medium text-sm flex justify-center items-center gap-2 disabled:opacity-50">
                            {saving && <Loader2 size={14} className="animate-spin" />} Salva e approva
                        </button>
                    </div>
                </div>
            )}
        </Modal>
    );
};
